import { useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * The workspace card used across the deal (and, later, contact) detail views: a white,
 * rounded, bordered panel with an optional title bar and a right-aligned action slot.
 *
 * When `collapsible` is set the title bar toggles the body open/closed; `defaultOpen`
 * chooses the initial state. A non-collapsible card just renders its header and body.
 * `bodyClassName` overrides the default body padding (e.g. tables that manage their own).
 */
export default function Card({
  title,
  action,
  collapsible = false,
  defaultOpen = true,
  className,
  bodyClassName,
  children,
}: {
  title?: ReactNode
  /** Right-aligned control(s) in the title bar (e.g. "+ Add task", a filter dropdown). */
  action?: ReactNode
  collapsible?: boolean
  defaultOpen?: boolean
  className?: string
  bodyClassName?: string
  children?: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  const showBody = !collapsible || open

  return (
    <section className={cn('bg-white rounded-xl border border-gray-200 shadow-sm', className)}>
      {title && (
        <div
          className={cn(
            'flex items-center justify-between gap-2 px-4 py-2.5',
            showBody && 'border-b border-gray-100',
            collapsible && 'cursor-pointer select-none',
          )}
          onClick={collapsible ? () => setOpen((v) => !v) : undefined}
        >
          <h3 className="text-sm font-semibold text-brand-darkest min-w-0 truncate">{title}</h3>
          <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
            {action}
            {collapsible && (
              <ChevronDown
                size={16}
                className={cn('text-gray-400 transition-transform', !open && '-rotate-90')}
              />
            )}
          </div>
        </div>
      )}
      {showBody && <div className={bodyClassName ?? 'p-4'}>{children}</div>}
    </section>
  )
}
